import { useEffect, useState } from "react";
import { getRendezVousTechnicien } from "../../api/techniciens.api";
import EtatBadge from "../../components/RendezVous/EtatBadge";
import {
  updateEtatRendezVous,
  updateRemarque,
} from "../../api/rendezVousService.api";

import "./Dash.css";

const formatDateFR = (dateISO) => {
  if (!dateISO) return "";

  const date = dateISO.includes("T")
    ? dateISO.split("T")[0]
    : dateISO;

  const [y, m, d] = date.split("-");

  return `${d}/${m}/${y}`;
};

const calculerHeures = (heureDebut, heureFin) => {
  if (!heureDebut || !heureFin) return 0;

  const [h1, m1] = heureDebut.split(":").map(Number);
  const [h2, m2] = heureFin.split(":").map(Number);

  const debut = h1 * 60 + m1;
  const fin = h2 * 60 + m2;

  if (fin <= debut) return 0;

  return ((fin - debut) / 60).toFixed(2);
};

export default function MesRendezVous() {
  const [rdvs, setRdvs] = useState([]);

  const [filters, setFilters] = useState({
    client: "",
    etat: "",
    date: "",
    month: "",
    page: 1,
    limit: 10,
  });

  const [totalPages, setTotalPages] = useState(1);
  const [loadingEtat, setLoadingEtat] = useState(null);

  // ID du rendez-vous en cours de modification
  const [editingRemarque, setEditingRemarque] = useState(null);

  // Texte temporaire de la remarque
  const [remarqueValue, setRemarqueValue] = useState("");

  const [savingRemarque, setSavingRemarque] = useState(false);

  // ==========================================
  // CHARGEMENT DES RENDEZ-VOUS
  // ==========================================
  const loadRDV = async () => {
    try {
      const res = await getRendezVousTechnicien(filters);

      setRdvs(res.data.data);

      setTotalPages(res.data.totalPages);
    } catch (err) {
      console.error("Erreur chargement rendez-vous :", err);
    }
  };

  useEffect(() => {
    loadRDV();
  }, [filters]);

  // ==========================================
  // FILTRES
  // ==========================================
  const updateFilter = (key, value) => {
    setFilters({
      ...filters,
      [key]: value,
      page: 1,
    });
  };

  const changePage = (newPage) => {
    setFilters({
      ...filters,
      page: newPage,
    });
  };

  // ==========================================
  // CHANGEMENT ETAT
  // ==========================================
  const onChangeEtat = async (id, newEtat) => {
    try {
      setLoadingEtat(id);

      await updateEtatRendezVous(id, {
        etat_travaux: newEtat,
      });

      await loadRDV();
    } catch (err) {
      console.error("Erreur changement état :", err);
    } finally {
      setLoadingEtat(null);
    }
  };

  // ==========================================
  // OUVRIR MODIFICATION REMARQUE
  // ==========================================
  const startEditRemarque = (rdv) => {
    setEditingRemarque(rdv.id);
    setRemarqueValue(rdv.remarque || "");
  };

  // ==========================================
  // ANNULER MODIFICATION
  // ==========================================
  const cancelEditRemarque = () => {
    setEditingRemarque(null);
    setRemarqueValue("");
  };

  // ==========================================
  // SAUVEGARDER REMARQUE
  // ==========================================
  const onSaveRemarque = async (id) => {
    try {
      setSavingRemarque(true);

      await updateRemarque(id, {
        remarque: remarqueValue.trim(),
      });

      // Mise à jour immédiate dans le tableau
      setRdvs((prev) =>
        prev.map((rv) =>
          rv.id === id
            ? {
                ...rv,
                remarque: remarqueValue.trim(),
              }
            : rv
        )
      );

      setEditingRemarque(null);
      setRemarqueValue("");

    } catch (err) {
      console.error("Erreur sauvegarde remarque :", err);

      alert("Erreur lors de la sauvegarde de la remarque");
    } finally {
      setSavingRemarque(false);
    }
  };

  return (
    <div className="mes-rendez-vous">

      <h2>📅 Mes rendez-vous</h2>

      {/* ======================================
          FILTRES
      ====================================== */}
      <div className="filters">

        <input
          type="text"
          placeholder="Client"
          value={filters.client}
          onChange={(e) =>
            updateFilter("client", e.target.value)
          }
        />

        <select
          value={filters.etat}
          onChange={(e) =>
            updateFilter("etat", e.target.value)
          }
        >
          <option value="">
            Tous les états
          </option>

          <option value="EN_ATTENTE">
            En attente
          </option>

          <option value="EN_COURS">
            En cours
          </option>

          <option value="TERMINE">
            Terminé
          </option>
        </select>

        <input
          type="date"
          value={filters.date}
          onChange={(e) =>
            updateFilter("date", e.target.value)
          }
        />

        <input
          type="month"
          value={filters.month}
          onChange={(e) =>
            updateFilter("month", e.target.value)
          }
        />

        <button onClick={loadRDV}>
          Filtrer
        </button>

      </div>

      {/* ======================================
          TABLE
      ====================================== */}
      <table className="responsive-table">

        <thead>
          <tr>
            <th>Client</th>
            <th>Téléphone</th>
            <th>Adresse</th>
            <th>Date</th>
            <th>Heure début</th>
            <th>Type</th>
            <th>Note</th>
            <th>Heure fin</th>
            <th>État</th>
            <th>Remarque</th>
            <th>Heures</th>
          </tr>
        </thead>

        <tbody>

          {rdvs.length === 0 ? (

            <tr>
              <td colSpan="11">
                Aucun rendez-vous
              </td>
            </tr>

          ) : (

            rdvs.map((r) => (

              <tr key={r.id}>

                {/* CLIENT */}
                <td>
                  {r.client}
                </td>

                {/* TELEPHONE */}
                <td>
                  <a href={`tel:${r.telephone}`}>
                    {r.telephone}
                  </a>
                </td>

                {/* ADRESSE */}
                <td>
                  {r.adresse || "-"}
                </td>

                {/* DATE */}
                <td>
                  {formatDateFR(r.date_rdv)}
                </td>

                {/* HEURE DEBUT */}
                <td>
                  {r.heure_debut}
                </td>

                {/* TYPE */}
                <td>
                  {r.type_entretien}
                </td>

                {/* =================================
                    NOTE ADMINISTRATEUR
                ================================= */}
                <td className="info-cell">

                  {r.note ? (

                    <span className="info-tooltip">

                      <span className="info-icon note-icon">
                        📝
                      </span>

                      <span className="tooltip-content">

                        <strong>
                          Note administrateur
                        </strong>

                        <span className="tooltip-text">
                          {r.note}
                        </span>

                      </span>

                    </span>

                  ) : (

                    <span className="empty-value">
                      —
                    </span>

                  )}

                </td>

                {/* HEURE FIN */}
                <td>
                  {r.heure_fin || "-"}
                </td>

                {/* =================================
                    ETAT
                ================================= */}
                <td>

                  {r.etat_travaux === "TERMINE" ? (

                    <EtatBadge
                      etat={r.etat_travaux}
                    />

                  ) : (

                    <select
                      disabled={loadingEtat === r.id}
                      value={r.etat_travaux}
                      onChange={(e) =>
                        onChangeEtat(
                          r.id,
                          e.target.value
                        )
                      }
                    >

                      <option value="EN_ATTENTE">
                        En attente
                      </option>

                      <option value="EN_COURS">
                        En cours
                      </option>

                      <option value="TERMINE">
                        Terminé
                      </option>

                    </select>

                  )}

                </td>

                {/* =================================
                    REMARQUE TECHNICIEN
                ================================= */}
                <td className="remarque-cell">

                  {editingRemarque === r.id ? (

                    // ==============================
                    // MODE MODIFICATION
                    // ==============================
                    <div className="remarque-edit">

                      <textarea
                        value={remarqueValue}
                        onChange={(e) =>
                          setRemarqueValue(e.target.value)
                        }
                        placeholder="Écrire une remarque..."
                        rows="3"
                        autoFocus
                      />

                      <div className="remarque-actions">

                        <button
                          type="button"
                          className="btn-save-remarque"
                          disabled={savingRemarque}
                          onClick={() =>
                            onSaveRemarque(r.id)
                          }
                          title="Enregistrer"
                        >
                          {savingRemarque ? "⏳" : "💾"}
                        </button>

                        <button
                          type="button"
                          className="btn-cancel-remarque"
                          disabled={savingRemarque}
                          onClick={cancelEditRemarque}
                          title="Annuler"
                        >
                          ❌
                        </button>

                      </div>

                    </div>

                  ) : (

                    // ==============================
                    // MODE AFFICHAGE
                    // ==============================
                    <div className="remarque-container">

                      {r.remarque ? (

                        <span className="info-tooltip">

                          <span className="info-icon remarque-icon">
                            🔧
                          </span>

                          <span className="tooltip-content">

                            <strong>
                              Remarque technicien
                            </strong>

                            <span className="tooltip-text">
                              {r.remarque}
                            </span>

                          </span>

                        </span>

                      ) : (

                        <span className="empty-value">
                          —
                        </span>

                      )}

                      {/* ICONE MODIFIER */}
                      <button
                        type="button"
                        className="btn-edit-remarque"
                        onClick={() =>
                          startEditRemarque(r)
                        }
                        title={
                          r.remarque
                            ? "Modifier la remarque"
                            : "Ajouter une remarque"
                        }
                      >
                        ✏️
                      </button>

                    </div>

                  )}

                </td>

                {/* =================================
                    HEURES
                ================================= */}
                <td>

                  {r.heure_fin
                    ? calculerHeures(
                        r.heure_debut,
                        r.heure_fin
                      ) + " h"
                    : "-"}

                </td>

              </tr>

            ))

          )}

        </tbody>

      </table>

      {/* ======================================
          PAGINATION
      ====================================== */}
      <div className="pagination">

        <button
          disabled={filters.page <= 1}
          onClick={() =>
            changePage(filters.page - 1)
          }
        >
          Précédent
        </button>

        <span>
          Page {filters.page} / {totalPages}
        </span>

        <button
          disabled={filters.page >= totalPages}
          onClick={() =>
            changePage(filters.page + 1)
          }
        >
          Suivant
        </button>

      </div>

    </div>
  );
}
